import { ReactNode } from "react";
import { Flex, GlobalToken, Typography } from "antd";
import styled, { css } from "styled-components";
import { Card, SectionTitle } from "@libertify/ui";
import { useAntTheme } from "hooks/useAntTheme";

type ProfileSectionTone = "default" | "danger";

type ProfileSectionCardProps = Readonly<{
  /**
   * Anchor target for the left panel, without the leading `#`. Omitted for
   * sections the nav doesn't link to.
   */
  id?: string;
  title: string;
  subtitle?: string;
  /** Rendered before the title, e.g. the user avatar. */
  icon?: ReactNode;
  /** Rendered at the end of the header row, e.g. a section-level action. */
  action?: ReactNode;
  tone?: ProfileSectionTone;
  children?: ReactNode;
}>;

type StyledToneProps = {
  $token: GlobalToken;
  $tone: ProfileSectionTone;
};

/**
 * Shared shell for every section of the profile page: an anchor target plus a
 * card with a consistent header.
 */
export const ProfileSectionCard = ({
  id,
  title,
  subtitle,
  icon,
  action,
  tone = "default",
  children,
}: ProfileSectionCardProps) => {
  const { theme } = useAntTheme();

  return (
    <section id={id}>
      <StyledCard
        padding="spacious"
        shadow="none"
        $token={theme}
        $tone={tone}
      >
        <Flex vertical gap={24}>
          <Flex align="center" justify="space-between" gap={16}>
            <Flex align="center" gap={16}>
              {icon}
              <Flex vertical gap={2}>
                <StyledTitle $token={theme} $tone={tone}>
                  {title}
                </StyledTitle>
                {subtitle ? (
                  <StyledSubtitle $token={theme} $tone={tone}>
                    {subtitle}
                  </StyledSubtitle>
                ) : null}
              </Flex>
            </Flex>
            {action}
          </Flex>
          {children}
        </Flex>
      </StyledCard>
    </section>
  );
};

// ─── Styled components ───

const StyledCard = styled(Card)<StyledToneProps>`
  ${({ $tone, $token }) =>
    $tone === "danger" &&
    css`
      background: ${$token.colorErrorBg};
      border-color: ${$token.colorErrorBorder};
    `}
`;

// The danger tone only swaps the color.
const StyledTitle = styled(SectionTitle)<StyledToneProps>`
  ${({ $tone, $token }) =>
    $tone === "danger" ? `color: ${$token.colorErrorText};` : ""}
`;

const StyledSubtitle = styled(Typography.Text)<StyledToneProps>`
  && {
    ${({ $tone, $token }) =>
      $tone === "danger" ? `color: ${$token.colorErrorText};` : ""}
  }
`;
